import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { usuarioService } from '../services/usuario.service';
import { useAuth } from '../hooks/useAuth';

type Theme = 'oscuro' | 'claro' | 'sistema';

// Colores personalizados para cada tema
interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
}

const defaultDarkColors: ThemeColors = {
  primary: '#1A237E',
  secondary: '#283593',
  accent: '#3949AB',
  background: '#0A0E27',
  surface: '#141832',
  text: '#E8EAF6',
};

const defaultLightColors: ThemeColors = {
  primary: '#1A237E',
  secondary: '#283593',
  accent: '#3949AB',
  background: '#F5F7FF',
  surface: '#FFFFFF',
  text: '#1A237E',
};

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
  darkColors: ThemeColors;
  lightColors: ThemeColors;
  setDarkColors: (colors: Partial<ThemeColors>) => void;
  setLightColors: (colors: Partial<ThemeColors>) => void;
  isLoading: boolean;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(
  undefined,
);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Obtener usuario autenticado (ThemeProvider está dentro de AuthProvider)
  const { user } = useAuth();

  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('theme');
    return (stored as Theme) || 'oscuro';
  });

  const [darkColors, setDarkColors] = useState<ThemeColors>(() => {
    const stored = localStorage.getItem('darkColors');
    return stored ? JSON.parse(stored) : defaultDarkColors;
  });

  const [lightColors, setLightColors] = useState<ThemeColors>(() => {
    const stored = localStorage.getItem('lightColors');
    return stored ? JSON.parse(stored) : defaultLightColors;
  });

  // Iniciar en loading si hay token almacenado (evita flash de colores incorrectos)
  const [isLoading, setIsLoading] = useState(() => !!localStorage.getItem('token'));

  const isDark =
    theme === 'oscuro' ||
    (theme === 'sistema' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Cargar preferencias del usuario desde el backend cada vez que cambia el usuario
  // Esto garantiza que al hacer login con otro usuario se carguen SUS colores
  useEffect(() => {
    const loadPreferencias = async () => {
      // Si no hay usuario autenticado, resetear a colores por defecto
      if (!user) {
        setDarkColors(defaultDarkColors);
        setLightColors(defaultLightColors);
        setTheme('oscuro');
        // Limpiar localStorage para que el próximo usuario empiece limpio
        localStorage.removeItem('darkColors');
        localStorage.removeItem('lightColors');
        localStorage.removeItem('theme');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await usuarioService.getPreferencias();
        if (response.success && response.data) {
          const prefs = response.data;

          // Cargar tema
          if (prefs.tema) {
            const temaLower = prefs.tema.toLowerCase();
            if (
              temaLower === 'oscuro' ||
              temaLower === 'claro' ||
              temaLower === 'sistema'
            ) {
              setTheme(temaLower as Theme);
            }
          }

          // Cargar colores dark
          if (prefs.coloresDark) {
            setDarkColors({
              primary: prefs.coloresDark.primary || defaultDarkColors.primary,
              secondary:
                prefs.coloresDark.secondary || defaultDarkColors.secondary,
              accent: prefs.coloresDark.accent || defaultDarkColors.accent,
              background:
                prefs.coloresDark.background || defaultDarkColors.background,
              surface: prefs.coloresDark.surface || defaultDarkColors.surface,
              text: prefs.coloresDark.text || defaultDarkColors.text,
            });
          } else {
            // Sin colores guardados → usar defaults
            setDarkColors(defaultDarkColors);
          }

          // Cargar colores light
          if (prefs.coloresLight) {
            setLightColors({
              primary: prefs.coloresLight.primary || defaultLightColors.primary,
              secondary:
                prefs.coloresLight.secondary || defaultLightColors.secondary,
              accent: prefs.coloresLight.accent || defaultLightColors.accent,
              background:
                prefs.coloresLight.background || defaultLightColors.background,
              surface: prefs.coloresLight.surface || defaultLightColors.surface,
              text: prefs.coloresLight.text || defaultLightColors.text,
            });
          } else {
            // Sin colores guardados → usar defaults
            setLightColors(defaultLightColors);
          }
        }
      } catch (error) {
        console.error('Error al cargar preferencias:', error);
        // En caso de error, usar defaults
        setDarkColors(defaultDarkColors);
        setLightColors(defaultLightColors);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferencias();
  // Se ejecuta cada vez que cambia el usuario (login/logout/cambio de usuario)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Determinar qué colores usar según el tema actual
  const currentColors = isDark ? darkColors : lightColors;

  // Aplicar colores CSS variables y clase dark
  useEffect(() => {
    const root = document.documentElement;

    // Aplicar colores activos según el tema
    Object.entries(currentColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Siempre mantener colores dark y light disponibles
    Object.entries(darkColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}-dark`, value);
    });

    Object.entries(lightColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}-light`, value);
    });

    // Aplicar clase dark al root
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Guardar en localStorage
    localStorage.setItem('theme', theme);
    localStorage.setItem('darkColors', JSON.stringify(darkColors));
    localStorage.setItem('lightColors', JSON.stringify(lightColors));
  }, [theme, isDark, darkColors, lightColors, currentColors]);

  // Guardar colores en el backend cuando cambien
  const handleSetDarkColors = async (colors: Partial<ThemeColors>) => {
    const newColors = { ...darkColors, ...colors };
    setDarkColors(newColors);

    // Guardar en backend
    try {
      await usuarioService.guardarColores({
        coloresDark: newColors,
      });
    } catch (error) {
      console.error('Error al guardar colores dark:', error);
    }
  };

  const handleSetLightColors = async (colors: Partial<ThemeColors>) => {
    const newColors = { ...lightColors, ...colors };
    setLightColors(newColors);

    // Guardar en backend
    try {
      await usuarioService.guardarColores({
        coloresLight: newColors,
      });
    } catch (error) {
      console.error('Error al guardar colores light:', error);
    }
  };

  // Guardar tema en backend cuando cambie
  const handleSetTheme = async (newTheme: Theme) => {
    setTheme(newTheme);

    try {
      await usuarioService.actualizarPreferencias({
        tema: newTheme.toUpperCase(),
      });
    } catch (error) {
      console.error('Error al guardar tema:', error);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme: handleSetTheme,
        isDark,
        darkColors,
        lightColors,
        setDarkColors: handleSetDarkColors,
        setLightColors: handleSetLightColors,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme debe usarse dentro de ThemeProvider');
  }
  return context;
};
